/**
 * Tela inicial: ID e PIN do operador direto na ticketeira.
 *
 * Não há escolha de empresa — ela vem fixada no flavor. O logo e o domínio no
 * rodapé existem para o operador não errar de app quando o aparelho tem os dois
 * validadores instalados.
 */

import React, {useState} from 'react';
import {
  ActivityIndicator,
  Pressable,
  Text,
  View,
  type LayoutChangeEvent,
} from 'react-native';
import {SafeAreaView} from 'react-native-safe-area-context';
import {ArrowRight, Lock, Ticket} from 'lucide-react-native';
import type {NativeStackScreenProps} from '@react-navigation/native-stack';

import {autenticar, ErroApi} from '../api/cliente';
import {ticketeira} from '../config';
import {semearDemonstracao} from '../dados/demonstracao';
import {ehDemonstracao, OPERADOR_DEMONSTRACAO} from '../dominio/demonstracao';
import {idDoAparelho, salvarSessao} from '../sessao';
import {BotaoIdioma} from '../ui/BotaoIdioma';
import {ConviteInstalar} from '../ui/ConviteInstalar';
import {Marca} from '../ui/Marca';
import {Teclado} from '../ui/Teclado';
import {Versao} from '../ui/Versao';
import {txt, useIdioma} from '../ui/idioma';
import {estilos, fontes, useTema} from '../ui/tema';
import type {ParamsPilha} from '../navegacao';

type Props = NativeStackScreenProps<ParamsPilha, 'Login'>;

/** Os três vãos de 10 px entre as quatro filas de teclas. */
const FOLGA_DO_TECLADO = 30;

/**
 * Abaixo desta altura útil a tela entra em modo apertado: logo menor, campos
 * mais rasos, menos respiro. Medido pelo que sobra para o miolo, já sem o
 * rodapé do Entrar — é o número em que o teclado deixaria de caber com folga
 * num Poco com a barra do navegador aberta.
 */
const ALTURA_APERTADA = 600;

type Campo = 'id' | 'pin';

export function TelaLogin({navigation}: Props) {
  const empresa = ticketeira();
  const t = useTema();
  useIdioma();

  const [usuarioId, setUsuarioId] = useState('');
  const [pin, setPin] = useState('');
  const [campo, setCampo] = useState<Campo>('id');
  const [entrando, setEntrando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [alturaDaTecla, setAlturaDaTecla] = useState(52);
  const [alturaUtil, setAlturaUtil] = useState(0);

  /**
   * Tela curta encolhe tudo um pouco, não só o teclado.
   *
   * A decisão sai da altura do miolo inteiro, que não muda com ela — se
   * saísse do espaço que sobra para o teclado, encolher o logo devolveria
   * espaço, o modo se desligaria, e a tela ficaria piscando entre os dois.
   */
  const apertado = alturaUtil > 0 && alturaUtil < ALTURA_APERTADA;

  /**
   * O logo é quadrado, então ele é o maior bloco fixo desta tela — 132 px de
   * altura, mais que qualquer campo. Em vez de dois tamanhos com um degrau no
   * meio, ele acompanha a tela: no máximo um quinto da altura útil, nunca
   * passando dos 132 nem caindo dos 64, que é onde a marca ainda se reconhece
   * de relance.
   */
  const larguraDoLogo = alturaUtil
    ? Math.round(Math.max(64, Math.min(132, alturaUtil * 0.2)))
    : 132;

  const podeEntrar = usuarioId.length > 0 && pin.length > 0 && !entrando;

  /**
   * A tecla se mede pelo espaço que sobrou, não por um número escolhido aqui.
   *
   * Aparelho alto ganha tecla grande; aparelho baixo, ou navegador com barra
   * de endereço gorda, encolhe até a última fila caber.
   *
   * Não há piso: um piso faria o teclado ficar maior que o espaço e transbordar
   * para cima, em cima do PIN — que é exatamente o campo que o operador precisa
   * enxergar enquanto digita. Tecla miúda ele ainda acerta; campo coberto, não.
   * O teto de 56 existe porque tecla gigante não ajuda ninguém.
   */
  function medirTeclado(e: LayoutChangeEvent) {
    const sobrou = e.nativeEvent.layout.height;
    if (sobrou > 0) {
      // O 22 não é um piso de conforto: é só para a altura não chegar
      // negativa ao estilo. Quem cair nele está com a tela deitada ou com a
      // fonte do sistema no máximo, e aí nada aqui salva o login.
      setAlturaDaTecla(Math.max(22, Math.min(56, Math.floor((sobrou - FOLGA_DO_TECLADO) / 4))));
    }
  }

  function digitar(digito: string) {
    setErro(null);
    if (campo === 'id') {
      setUsuarioId(atual => atual + digito);
    } else {
      setPin(atual => atual + digito);
    }
  }

  function apagar() {
    if (campo === 'id') {
      setUsuarioId(atual => atual.slice(0, -1));
    } else {
      setPin(atual => atual.slice(0, -1));
    }
  }

  function limpar() {
    if (campo === 'id') {
      setUsuarioId('');
    } else {
      setPin('');
    }
  }

  async function entrar() {
    setErro(null);
    setEntrando(true);
    try {
      // A conta de demonstração é reconhecida ANTES da rede, porque ela não
      // existe no servidor de nenhuma ticketeira — e porque quem avalia o app
      // (a revisão da Play, uma apresentação comercial) costuma estar sem
      // acesso à rede da portaria. Ver src/dominio/demonstracao.ts.
      if (ehDemonstracao(usuarioId, pin)) {
        await semearDemonstracao();
        await salvarSessao({
          usuarioId: OPERADOR_DEMONSTRACAO.usuarioId,
          nome: OPERADOR_DEMONSTRACAO.nome,
          token: '',
          machineId: 0,
          ticketeira: empresa.id,
          autenticadoEm: new Date().toISOString(),
          demonstracao: true,
        });
        navigation.replace('Caixa');
        return;
      }

      const aparelho = await idDoAparelho();
      const {sessao, machineId, token} = await autenticar(usuarioId, pin, aparelho);

      await salvarSessao({
        usuarioId,
        nome: sessao.nome ?? usuarioId,
        token,
        machineId,
        ticketeira: empresa.id,
        autenticadoEm: new Date().toISOString(),
      });

      navigation.replace('Caixa');
    } catch (e) {
      // PIN errado some da tela: o próximo toque recomeça, não emenda.
      setPin('');
      setCampo('pin');
      setErro(e instanceof ErroApi ? e.message : txt('Não foi possível entrar. Tente de novo.'));
    } finally {
      setEntrando(false);
    }
  }

  /** Moldura do campo: a borda de marca marca qual dos dois recebe o dígito. */
  const moldura = (dele: Campo) => ({
    backgroundColor: t.superficie,
    borderWidth: campo === dele ? 2 : 1,
    borderColor: campo === dele ? t.primaria : t.borda,
    borderRadius: 14,
    paddingHorizontal: 18,
    paddingVertical: apertado ? 11 : 16,
  });

  return (
    <SafeAreaView style={[estilos.tela, {backgroundColor: t.fundo}]}>
      {/* Sem rolagem: nada aqui é campo de texto — ID e PIN são toques no
          teclado abaixo —, então a altura não muda durante o uso e a tela
          inteira cabe de uma vez. Rolar para achar a tecla 0 é o tipo de
          coisa que o operador descobre com a fila já formada. */}
      <View
        onLayout={e => setAlturaUtil(e.nativeEvent.layout.height)}
        style={{
          flex: 1,
          paddingHorizontal: 24,
          paddingTop: apertado ? 10 : 20,
          paddingBottom: 10,
          gap: apertado ? 10 : 16,
        }}>
        <View style={{gap: apertado ? 7 : 10}}>
          {/* A marca de um lado, o idioma do outro: o canto de cima é onde o
              operador procura ajuste de aparelho, e ali ele não disputa espaço
              com o caminho de entrar. */}
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: 12,
            }}>
            <Marca largura={larguraDoLogo} />
            <BotaoIdioma />
          </View>

          <View style={{flexDirection: 'row'}}>
            <View
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                gap: 6,
                paddingHorizontal: 10,
                paddingVertical: 6,
                borderRadius: 8,
                backgroundColor: t.superficie,
                borderWidth: 1,
                borderColor: t.borda,
              }}>
              <Ticket size={14} color={t.primaria} strokeWidth={2} />
              <Text
                style={{
                  color: t.primaria,
                  fontSize: 12,
                  fontFamily: fontes.negrito,
                  letterSpacing: 0.7,
                  textTransform: 'uppercase',
                }}>
                {txt('Validador')}
              </Text>
            </View>
          </View>
        </View>

        <View style={{gap: apertado ? 7 : 10}}>
          <View style={{gap: apertado ? 4 : 6}}>
            <Text style={[estilos.rotulo, {color: t.textoFraco}]}>{txt('ID do operador')}</Text>
            <Pressable onPress={() => setCampo('id')} style={moldura('id')}>
              <Text
                style={{
                  color: usuarioId ? t.texto : t.textoApagado,
                  fontSize: 22,
                  fontFamily: fontes.semi,
                  fontVariant: ['tabular-nums'],
                }}>
                {usuarioId || txt('Seu ID')}
              </Text>
            </Pressable>
          </View>

          <View style={{gap: apertado ? 4 : 6}}>
            <Text style={[estilos.rotulo, {color: t.textoFraco}]}>{txt('PIN')}</Text>
            <Pressable
              onPress={() => setCampo('pin')}
              style={[
                moldura('pin'),
                {
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 11,
                  minHeight: apertado ? 44 : 54,
                },
              ]}>
              {/* Bolinha por dígito, com quatro vagas de piso: o operador confere
                  quantas apertou sem o PIN aparecer para a fila atrás dele. */}
              {Array.from({length: Math.max(4, pin.length)}).map((_, i) => (
                <View
                  key={i}
                  style={{
                    width: 12,
                    height: 12,
                    borderRadius: 6,
                    backgroundColor: i < pin.length ? t.texto : t.borda,
                  }}
                />
              ))}
            </Pressable>
          </View>
        </View>

        {/* O teclado fica com o que sobrar da tela e se mede por ela: em
            aparelho alto as teclas crescem, em aparelho baixo encolhem até
            caber. Antes eram 52 px fixos, e onde a tela era curta a última
            fila — LIMPAR, 0 e apagar — ficava embaixo do botão Entrar. */}
        <View style={{flex: 1, justifyContent: 'flex-end'}} onLayout={medirTeclado}>
          {/* Só depois da primeira medida. Desenhar antes seria desenhar com a
              altura padrão, que em tela curta cobriria o PIN por um quadro —
              um susto de nada, mas justo na tela que o operador abre com a
              fila chegando. */}
          {alturaUtil > 0 ? (
            <Teclado
              aoDigitar={digitar}
              aoApagar={apagar}
              aoLimpar={limpar}
              // Teclado físico: Enter no ID passa para o PIN, no PIN entra.
              aoConfirmar={() => (campo === 'id' ? setCampo('pin') : podeEntrar && entrar())}
              altura={alturaDaTecla}
            />
          ) : null}
        </View>
      </View>

      {/* Entrar fica fora do bloco que se ajusta. Neste terminal o conteúdo
          passa da altura útil da tela e o botão caía abaixo da dobra,
          aparecendo pela metade
          atrás da barra de navegação — e login que exige rolar é turno que
          começa devagar. Ancorado, ele não depende de o resto caber. */}
      <View style={{paddingHorizontal: 24, paddingBottom: 14, gap: 12}}>
        {erro ? (
          <Text style={[estilos.erro, {color: t.erro, textAlign: 'center'}]}>{erro}</Text>
        ) : null}

        <Pressable
          onPress={entrar}
          disabled={!podeEntrar}
          style={[
            estilos.botao,
            {backgroundColor: podeEntrar ? t.primaria : t.superficieAlta},
          ]}>
          {entrando ? (
            <ActivityIndicator color={t.tintaPrimaria} />
          ) : (
            <>
              <Text
                style={[
                  estilos.textoBotao,
                  {color: podeEntrar ? t.tintaPrimaria : t.textoApagado},
                ]}>
                {txt('Entrar')}
              </Text>
              <ArrowRight
                size={20}
                color={podeEntrar ? t.tintaPrimaria : t.textoApagado}
                strokeWidth={2.6}
              />
            </>
          )}
        </Pressable>

        {/* Só no navegador: some quando o validador já abre como app. */}
        <ConviteInstalar />

        <View style={{gap: 5}}>
          <View style={{flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 7}}>
            <Lock size={14} color={t.textoApagado} strokeWidth={2} />
            <Text style={{color: t.textoApagado, fontSize: 13, fontFamily: fontes.regular}}>
              {t.dominio}
            </Text>
          </View>
          {/* A versão antes do login: é onde o suporte pergunta primeiro. */}
          <Versao />
        </View>
      </View>
    </SafeAreaView>
  );
}
